/**
 * AccountModal.js — My Account modal shell
 *
 * Login entry point. On successful login:
 *   1. Signs in anonymously to Firebase Auth (grants Firestore access)
 *   2. Saves the safe user object to localStorage (session persistence)
 *   3. Navigates to /portal (full-screen dashboard)
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Portal from '../Portal';
import AccountDashboard from './AccountDashboard';
import AccountSetupModal from './portal/AccountSetupModal';

export const PORTAL_SESSION_KEY  = 'gohost_portal_user';
export const SESSION_EXPIRED_KEY = 'gohost_session_expired';

const AccountModal = ({ isOpen, onClose }) => {
  const [setupUser, setSetupUser] = useState(null); // set when first-login setup is needed

  if (!isOpen && !setupUser) return null;

  const handleLogin = ({ user, remember = true }) => {
    // remember=true  → localStorage  (survives browser close/restart)
    // remember=false → sessionStorage (clears when browser/tab closes)
    if (remember) {
      localStorage.setItem(PORTAL_SESSION_KEY, JSON.stringify(user));
      sessionStorage.removeItem(PORTAL_SESSION_KEY);
    } else {
      sessionStorage.setItem(PORTAL_SESSION_KEY, JSON.stringify(user));
      localStorage.removeItem(PORTAL_SESSION_KEY);
    }

    if (user.isTemporaryPassword) {
      setSetupUser(user);
      return;
    }

    window.location.href = '/portal';
  };

  // First-login setup modal — shown after successful login if isTemporaryPassword
  if (setupUser) {
    return (
      <AccountSetupModal
        user={setupUser}
        onComplete={() => { window.location.href = '/portal'; }}
      />
    );
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-40 select-none">
        {/* Backdrop */}
        <motion.div
          onClick={onClose}
          className="fixed inset-0 bg-black bg-opacity-75"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        {/* Card */}
        <motion.div
          onClick={(e) => e.stopPropagation()}
          className="fixed inset-0 flex justify-center items-center px-4"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <div
            className="bg-white border border-neutral-200 rounded-2xl shadow-2xl w-full overflow-hidden"
            style={{ maxWidth: '400px' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-gold">GoHost</p>
                <h2 className="text-base font-semibold text-neutral-800 leading-tight">Staff Portal</h2>
              </div>
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg border border-neutral-200 text-sm font-medium text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 transition-colors"
              >
                Close
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5">
              <AccountDashboard onLogin={handleLogin} />
            </div>
          </div>
        </motion.div>
      </div>
    </Portal>
  );
};

export default AccountModal;
